import { createMeToAvNewComponentNotification } from './createMeToAvNewComponentNotification';
import { getRowsTransitionedToMeDoneYes } from './meDoneTransitions';

type ProfitCenterOption = { label: string; value: number };

export type ProcessMeDoneNotificationsParams = {
  editedRow: any[];
  editedRowOriginal: Record<string, Record<string, unknown>>;
  allAccessibleColumns: any[];
  hasMeRole: boolean;
  screenGridId: number;
  createdBy: number;
  configSchema: string;
};

/**
 * Returns the configured value-option id for `erledigt_me = yes`.
 *
 * @param allAccessibleColumns Grid column metadata including optional value options.
 * @returns Numeric id for the "yes" option or `null` when the option is not configured.
 */
function getMeDoneYesId(allAccessibleColumns: any[]): number | null {
  const workPrepDoneValueOptions = allAccessibleColumns.find(
    (col) => col.attrInternalName === 'erledigt_me',
  )?.valueOptions;

  return workPrepDoneValueOptions?.find((item) => item.label.toLowerCase() === 'yes')?.value ?? null;
}

/**
 * Creates "ME to AV new component" notifications for rows that transition
 * to `erledigt_me = yes` in the current edit batch.
 *
 * Behavior is intentionally fail-safe:
 * - Returns `null` when there is nothing to do (no edited rows/no ME role/no yes id).
 * - Returns `null` after successful notification creation.
 * - Returns an error string if any notification creation fails.
 */
export async function processMeDoneNotifications({
  editedRow,
  editedRowOriginal,
  allAccessibleColumns,
  hasMeRole,
  screenGridId,
  createdBy,
  configSchema,
}: ProcessMeDoneNotificationsParams): Promise<string | null> {
  if (!editedRow.length || !hasMeRole) {
    return null;
  }

  const meDoneYesId = getMeDoneYesId(allAccessibleColumns);
  if (meDoneYesId == null) {
    return null;
  }

  const meDoneRows = getRowsTransitionedToMeDoneYes({
    editedRows: editedRow,
    editedRowsOriginal: editedRowOriginal,
    meDoneYesId,
  });

  const valueOptionsProfitCenter = (allAccessibleColumns.find(
    (col) => col.attrInternalName === 'pf_lc',
  )?.valueOptions ?? []) as ProfitCenterOption[];

  try {
    await Promise.all(
      meDoneRows.map((row) =>
        createMeToAvNewComponentNotification({
          row: row as {
            id1: number | string;
            htz?: string | number | null;
            pf_lc?: number | string | null;
          },
          screenGridId,
          createdBy,
          valueOptionsProfitCenter,
          configSchema,
        }),
      ),
    );
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : 'Notification creation failed';
  }
}